import React, { useState } from "react";
import FileCard from './FileCard';
import MarkdownIt from 'markdown-it';
import createDOMPurify from 'dompurify';

const md = new MarkdownIt({ html: true, linkify: true, typographer: true });
const DOMPurify = typeof window !== 'undefined' ? createDOMPurify(window) : { sanitize: (s: string) => s } as any;
import { Layout, Row, Col, Typography, Space, Button, message, Empty, Spin, List } from "antd";
import { RobotOutlined, EditOutlined, CopyOutlined } from "@ant-design/icons";
import { createAIConversation, updateAIConversation, getAIConversation } from "./resources";
import AIAssistantMessageInputField from "./components/AiAssistantMessageInputField/AIAssistantMessageInputField";

interface ScreeningAssistantPanelProps {
  layoutStyle: React.CSSProperties;
  headerStyle: React.CSSProperties;
  PanelStyle: React.CSSProperties;
  AItextingInputAreaStyle: React.CSSProperties;
  selectedConversation?: any | null;
  loadingConversation?: boolean;
  // Add callbacks for parent to update conversation
  onNewConversation?: (conv: any) => void;
}

const { Header, Content, Footer } = Layout;
const { Title } = Typography;


const ScreeningAssistantPanel: React.FC<ScreeningAssistantPanelProps> = ({
  layoutStyle,
  headerStyle,
  PanelStyle,
  AItextingInputAreaStyle,
  selectedConversation = null,
  loadingConversation = false,
  onNewConversation,
}) => {
  const [creating, setCreating] = useState(false);

  

  const handleNewChat = async () => {
    if (!selectedConversation?.ai_assistant?.id) {
      message.error("No assistant selected for new chat.");
      return;
    }
    setCreating(true);
    try {
      // Create a new conversation for this assistant
      const userData = localStorage.getItem("userData");
      let userId = null;
      if (userData) {
        try {
          userId = JSON.parse(userData)?.id;
        } catch {}
      }
      if (!userId) {
        message.error("No user ID found.");
        setCreating(false);
        return;
      }
      const payload = {
        title: "Untitled",
        user_id: userId,
        text: { messages: [] },
      };
      const res = await createAIConversation(selectedConversation.ai_assistant.id,payload);
      if (res?.data) {
        message.success("New chat created");
        if (onNewConversation) onNewConversation(res.data);
      } else {
        message.error("Failed to create new chat");
      }
    } catch (err) {
      message.error("Failed to create new chat");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  // Handler for sending a new user message
  const [sending, setSending] = useState(false);
  const handleSendMessage = async (userMessage) => {
    if (!selectedConversation?.id) return;

    // userMessage may be a string (legacy) or an object { content: [{type,text}], attachments: [...] }
    let newMessage: any;
    if (typeof userMessage === 'string') {
      newMessage = { role: 'user', content: [{ type: 'input_text', text: userMessage }] };
    } else if (userMessage && userMessage.content) {
      newMessage = { role: 'user', content: userMessage.content };
      if (userMessage.attachments) {
        // append attachments as additional content items
        newMessage.content = newMessage.content.concat(userMessage.attachments.map(a => {
          const isImage = a.type?.startsWith?.('image/') || (a.url && /\.(jpg|jpeg|png|gif|webp)$/i.test(a.url));
          return { type: isImage ? 'input_image' : 'input_file', file_id: a.url };
        }));
      }
    } else {
      return;
    }

    setSending(true);
    try {
      await updateAIConversation(selectedConversation.id, {
        title: selectedConversation.title,
        text: newMessage,
      });
      // Refresh conversation from API to get any assistant response
      if (selectedConversation.id) {
        const res = await getAIConversation(selectedConversation.id);
        if (res?.data) {
          if (onNewConversation) onNewConversation(res.data);
        }
      }
    } catch (err) {
      message.error("Failed to send message");
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout style={layoutStyle}>
      <Header style={{ ...headerStyle, padding: "3px 10px" }}>
        <Row align="middle" style={{ width: "100%" }}>
          <Col flex="auto">
            <Title level={4} style={{ margin: 0 }}>
              <RobotOutlined /> {selectedConversation ? selectedConversation.title : "Untitled"}
            </Title>
            <p
              dangerouslySetInnerHTML={{
                __html: selectedConversation
                  ? `By: <strong>${selectedConversation.ai_assistant.name || 'Unknown'}</strong> <em>${selectedConversation.ai_assistant.description || 'Unknown'}</em>`
                  : 'By: John Smith',
              }}
            />
          </Col>
          <Col>
            <Space>
              <Button
                type="primary"
                icon={<EditOutlined />}
                loading={creating}
                onClick={handleNewChat}
              >
                New Chat
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>
      <Content style={{ ...PanelStyle, background: '#fafafa', display: 'flex', flexDirection: 'column' }}>
        <>
          {loadingConversation ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
              <Spin tip="Loading conversation..." />
            </div>
          ) : selectedConversation ? (
            <>
              <div
                style={{ padding: '16px 10%', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}
                ref={el => {
                  if (el) {
                    // Scroll to bottom on mount/update
                    el.scrollTop = el.scrollHeight;
                  }
                }}
              >
                <div style={{ marginBottom: 12, textAlign: 'center', color: '#888' }}>
                  <strong>Conversation created:</strong> {new Date(selectedConversation.created_at).toLocaleString()}
                </div>
                <List
                  dataSource={selectedConversation?.text?.messages || []}
                  renderItem={(msg: any, idx) => {
                    const isUser = msg.role === 'user';
                    return (
                      <List.Item
                        key={idx}
                        style={{
                          display: msg.role ==='system' ? 'none' : 'flex',
                          borderRadius: 16,
                          marginBottom: 8,
                          justifyContent: isUser ? 'flex-end' : 'flex-start',
                          padding: 0,
                          border: 'none',
                        }}
                      >
                        <div
                          style={{
                            maxWidth: '70%',
                            minWidth: 60,
                            padding: '10px 16px',
                            borderRadius: 16,
                            background: isUser ? '#e6f7ff' : '#bed8a4ff',
                            color: '#222',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                            marginLeft: isUser ? 'auto' : 0,
                            marginRight: isUser ? 0 : 'auto',
                            textAlign: 'left',
                          }}
                        >
                          {/* <div style={{ fontWeight: 500, fontSize: 12, marginBottom: 2, textTransform: 'capitalize', color: '#888' }}>
                            {msg.role}
                          </div> */}
                          <div style={{ wordBreak: 'break-word' }}>
                            {Array.isArray(msg.content) ? (
                              msg.content.map((c, ci) => {
                                if (c.type === 'text') {
                                  const raw = md.render(c.text || '');
                                  const safe = DOMPurify.sanitize(raw);
                                  return <div key={ci} dangerouslySetInnerHTML={{ __html: safe }} />;
                                }
                                // treat input_image similar to input_file but allow FileCard to preview images when s3_url is present
                                if (c.type === 'input_file' || c.type === 'input_image') return <FileCard key={ci} openaiId={c.file_id || c.fileId || c.openai_file_id} />;
                                if (c.type === 'file' || c.type === 'attachment') {
                                  const isImg = c.mime?.startsWith?.('image/') || (c.url && /\.(jpg|jpeg|png|gif|webp)$/i.test(c.url));
                                  if (isImg) return <img key={ci} src={c.url} style={{ maxWidth: '100%', marginTop: 8, borderRadius: 6 }} />;
                                  return <div key={ci}><a href={c.url} target="_blank" rel="noreferrer">{c.name || c.url}</a></div>;
                                }
                                // fallback
                                const rawFallback = md.render(String(c.text || JSON.stringify(c)));
                                return <div key={ci} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rawFallback) }} />;
                              })
                              ) : (
                              msg.content && msg.content[0] && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(md.render(msg.content[0].text || '')) }} />
                            )}
                          </div>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              </div>
              <div style={AItextingInputAreaStyle}>
                <AIAssistantMessageInputField onSend={handleSendMessage} isSending={sending} conversationId={selectedConversation?.id} />
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, minHeight: 300, textAlign: 'center', flexDirection: 'column' }}>
              <Title level={5} style={{ margin: 0 }}>
                To begin, select an assistant and click on 'Chat'
              </Title>
            </div>
          )}
        </>
      </Content>
    </Layout>
  );
};

export default ScreeningAssistantPanel;
